import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, AlertCircle, HelpCircle, Star, Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import styles from './LoginPage.module.css';

type LoginMode = 'code' | 'password';

export default function LoginPage() {
  const [mode, setMode] = useState<LoginMode>('code');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { signInWithOtp, signInWithPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (mode === 'password' && !password) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);

    if (mode === 'code') {
      const { error: signInError } = await signInWithOtp(email);
      if (signInError) {
        setError(signInError.message || 'Unable to send verification code. Please try again.');
        setIsLoading(false);
        return;
      }
      navigate('/verify-otp', { state: { email } });
      return;
    }

    const { error: signInError } = await signInWithPassword(email, password);
    if (signInError) {
      setError(signInError.message || 'Incorrect email or password. Please try again.');
      setIsLoading(false);
      return;
    }
    navigate('/');
  };

  const switchMode = (next: LoginMode) => {
    if (next === mode) return;
    setMode(next);
    setError('');
    setPassword('');
  };

  return (
    <div className={styles.container}>
      <div className={styles.leftColumn}>
        <div className={styles.badgeRow}>
          <span className={styles.newBadge}>What's new</span>
          <span className={styles.badgeText}>We are on the app store</span>
        </div>
        
        <h1 className={styles.headline}>
          Healthy Starts with<br />
          <span className={styles.headlineAccent}>Recipes That Work.</span>
        </h1>
        
        <p className={styles.subtext}>
          Built on decades of trusted nutrition guidance, our recipes are designed to nourish, energize, and support your wellness — one meal at a time.
        </p>
        
        <div className={styles.socialProof}>
          <div className={styles.avatarGroup}>
            <div className={styles.avatarPlaceholder} style={{ background: '#e8d4f4' }}>J</div>
            <div className={styles.avatarPlaceholder} style={{ background: '#d4e8f4' }}>M</div>
            <div className={styles.avatarPlaceholder} style={{ background: '#f4e8d4' }}>S</div>
            <div className={styles.avatarPlaceholder} style={{ background: '#d4f4e8' }}>A</div>
          </div>
          <div className={styles.stars}>
            {[1, 2, 3, 4].map((i) => (
              <Star key={i} size={20} fill="#f59e0b" color="#f59e0b" />
            ))}
            <div className={styles.partialStar}>
              <Star size={20} fill="#e5e7eb" color="#e5e7eb" className={styles.starBackground} />
              <div className={styles.starFill} style={{ width: '62.5%' }}>
                <Star size={20} fill="#f59e0b" color="#f59e0b" />
              </div>
            </div>
          </div>
        </div>
        
        <div className={styles.appStores}>
          <a href="https://apps.apple.com/us/app/peapil-health/id6748030177" target="_blank" rel="noopener noreferrer" className={styles.storeButton}>
            <img src="/app-store-badge-black.avif" alt="Download on the App Store" className={styles.storeBadge} />
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.peapilpublishing.app" target="_blank" rel="noopener noreferrer" className={styles.storeButton}>
            <img src="/play-store-badge-black.avif" alt="Get it on Google Play" className={styles.storeBadge} />
          </a>
        </div>
      </div>
      
      <div className={styles.rightColumn}>
        <div className={styles.decorativeGraphic}>
          <svg viewBox="0 0 200 200" className={styles.burstSvg}>
            <g transform="translate(100, 100)">
              {[...Array(24)].map((_, i) => (
                <line
                  key={i}
                  x1="0"
                  y1="0"
                  x2={Math.cos((i * 15 * Math.PI) / 180) * 90}
                  y2={Math.sin((i * 15 * Math.PI) / 180) * 90}
                  stroke="#2d5a4a"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              ))}
            </g>
          </svg>
        </div>
        
        <div className={styles.card}>
          <div className={styles.header}>
            <img src="/peapil-logo.png" alt="Peapil Health" className={styles.logo} />
            <h2 className={styles.cardTitle}>Login and cook with confidence.</h2>
          </div>

          <div className={styles.tabs} role="tablist" aria-label="Sign in method">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'code'}
              className={`${styles.tab} ${mode === 'code' ? styles.tabActive : ''}`}
              onClick={() => switchMode('code')}
              disabled={isLoading}
            >
              Email Code
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'password'}
              className={`${styles.tab} ${mode === 'password' ? styles.tabActive : ''}`}
              onClick={() => switchMode('password')}
              disabled={isLoading}
            >
              Password
            </button>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            {error && (
              <div className={styles.error}>
                <AlertCircle size={20} />
                <span>{error}</span>
              </div>
            )}

            <div className={styles.inputGroup}>
              <label htmlFor="email" className={styles.label}>Email Address</label>
              <div className={styles.inputWrapper}>
                <Mail size={20} className={styles.inputIcon} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className={styles.input}
                  autoComplete="email"
                  disabled={isLoading}
                />
              </div>
            </div>

            {mode === 'password' && (
              <div className={styles.inputGroup}>
                <label htmlFor="password" className={styles.label}>Password</label>
                <div className={styles.inputWrapper}>
                  <Lock size={20} className={styles.inputIcon} />
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your password"
                    className={styles.input}
                    autoComplete="current-password"
                    disabled={isLoading}
                  />
                </div>
              </div>
            )}

            <button type="submit" className={styles.button} disabled={isLoading}>
              {isLoading
                ? (mode === 'code' ? 'Sending...' : 'Signing in...')
                : (mode === 'code' ? 'Send Code' : 'Sign In')}
            </button>
          </form>

          <div className={styles.cardFooter}>
            <Link to="/faq" className={styles.faqLink}>
              <HelpCircle size={16} />
              <span>Help & FAQ</span>
            </Link>
            
            <p className={styles.terms}>
              By signing in, you agree with our{' '}
              <a href="https://peapilpublishing.com/policies/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
